import {defineStore} from "pinia";
import {computed, ref, watch} from "vue";
import {useAnalyticsStore} from "@/Stores/AnalyticsStore";

// Initialize event listeners immediately when module loads (client-side only)
let initialized = false;

export const useWpaInstallStore = defineStore("wpa-install", () => {
	const enabled = ref(false);
	const show = ref(false);
	const prompt = ref(null);
	const mobile = computed(() => window.innerWidth < 768);
	const android = computed(() => !!navigator.userAgent.match(/Android/i));
	const pwa_visitor = computed(
		() => window.matchMedia('(display-mode: window-controls-overlay)').matches || window.matchMedia(
			'(display-mode: standalone)').matches);
	const analyticsStore = useAnalyticsStore();

	// Debug flag - set to true to force enable PWA slides for testing
	const debugForceEnable = ref(window.location.search.includes('debug_pwa=1'));

	watch(
		() => prompt.value,
		(value) => {
			enabled.value = debugForceEnable.value || (!pwa_visitor.value && !!value) || (mobile.value && !pwa_visitor.value);
		},
		{immediate: true},
	);

	// Initialize event listeners only once and only on client-side
	if (typeof window !== 'undefined' && !initialized) {
		initialized = true;

		window.addEventListener('beforeinstallprompt', async (event) => {
			// Prevent the default behavior to immediately show the prompt
			event.preventDefault();

			// store the event for later
			prompt.value = event;

			// Debug logging
			console.log('[PWA] beforeinstallprompt event captured and saved');
		});

		window.addEventListener('appinstalled', () => {
			prompt.value = null;
			console.log('[PWA] App was installed successfully');
		});

		console.log('[PWA] Event listeners initialized');
	}

	const showInAppInstallPromotion = async (media) => {

		if (prompt.value) {
			console.log('[PWA] Showing install prompt for:', media);

			// Show the prompt
			prompt.value.prompt();

			// Wait for the user to respond to the prompt
			const {outcome} = await prompt.value.userChoice;

			// Act on the user's choice
			if (outcome === 'accepted') {

				prompt.value = null;

				analyticsStore.sendAnalyticsEvent('pwa_install', {
					adType: 'PWA ' + media,
				});

				console.log('[PWA] User accepted install prompt');
			} else {
				if (outcome === 'dismissed') {
					analyticsStore.sendAnalyticsEvent('pwa_dismiss', {
						adType: 'PWA ' + media,
					});

					console.log('[PWA] User dismissed install prompt');
				}
			}
		} else {
			// Prompt not available - provide feedback
			console.warn('[PWA] Install prompt not available. Possible reasons:');
			console.warn('  - App already installed');
			console.warn('  - User previously dismissed prompt too many times');
			console.warn('  - beforeinstallprompt event not fired yet');
			console.warn('  - PWA criteria not met');
			console.warn('  - Browser does not support PWA installation');
		}
	};

	return {show, enabled, pwa_visitor, showInAppInstallPromotion};
});
